import {
  Cancel,
  CheckCircle,
  Close,
  ContentCopy,
  DescriptionOutlined,
  History,
  OpenInNew,
  Pending,
  PersonOutline,
  Schedule,
  SchoolOutlined,
  VisibilityOutlined,
  WorkOutline,
  HealthAndSafetyOutlined,
  FlightTakeoffOutlined,
  VerifiedOutlined,
} from "@mui/icons-material";
import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import axios from "const/axios";
import { P } from "const/permissions";
import { usePermission } from "hooks/usePermissions";
import { format, formatDistanceToNow, isValid, parseJSON } from "date-fns";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import useKothar from "context/useKothar";
import { buildAdmissionValuesFromStudent } from "../Accounts/accountUtils";
import AdmissionStatusHistory from "../Admissions/AddAdmissions/AdmissionStatusHistory";
import InsuranceStatusHistoryTab from "../Insurance/AddInsurance/InsuranceStatusHistoryTab";
import SkillAssessmentStatusHistoryTab from "../SkillAssessment/AddSkillAssessment/SkillAssessmentStatusHistoryTab";
import VisaStatusHistoryTab from "../Visa/AddVisa/VisaStatusHistoryTab";
import ReferralApplicationSection from "./ReferralApplicationSection";
import ApplicationProvenanceDialog from "./ApplicationProvenanceDialog";

const MODULE_META = {
  admission: {
    label: "Admission",
    short: "ADM",
    color: "primary",
    icon: SchoolOutlined,
    editPath: (id) => `/admin/admission/edit/${id}`,
    hasHistory: true,
  },
  visa: {
    label: "Visa",
    short: "VISA",
    color: "secondary",
    icon: FlightTakeoffOutlined,
    editPath: (id) => `/admin/visa/edit/${id}`,
    hasHistory: true,
  },
  insurance: {
    label: "Insurance",
    short: "INS",
    color: "info",
    icon: HealthAndSafetyOutlined,
    editPath: (id) => `/admin/insurance/edit/${id}`,
    hasHistory: true,
  },
  "skill-assessment": {
    label: "Skill Assessment",
    short: "SKILL",
    color: "warning",
    icon: WorkOutline,
    editPath: (id) => `/admin/skill-assessment/edit/${id}`,
    hasHistory: true,
  },
  "rpl-course": {
    label: "RPL",
    short: "RPL",
    color: "success",
    icon: VerifiedOutlined,
    editPath: (id) => `/admin/rpl-client/edit/${id}`,
    hasHistory: false,
  },
  other: {
    label: "Other",
    short: "OTH",
    color: "default",
    icon: DescriptionOutlined,
    editPath: (id) => `/admin/other/${id}`,
    hasHistory: false,
  },
};

const SUCCESS_STATUSES = new Set(["Visa Granted", "Certificate Ready"]);
const ERROR_STATUSES = new Set(["Visa Rejected", "Certificate Cancelled", "Cancelled"]);
const WAITING_STATUSES = new Set([
  "Visa Waiting",
  "Certificate Processing",
  "Placement Waiting",
  "Placement Processing",
]);

const formatSafeDate = (value, pattern = "dd MMM yyyy") => {
  if (!value) return null;
  const date = new Date(value);
  if (!isValid(date)) return null;
  return format(date, pattern);
};

const formatRelative = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (!isValid(date)) return null;
  return formatDistanceToNow(date, { addSuffix: true });
};

const getModuleMeta = (module) => MODULE_META[module] || MODULE_META.other;

const getStatusTone = (status) => {
  if (SUCCESS_STATUSES.has(status)) return "success";
  if (ERROR_STATUSES.has(status)) return "error";
  if (WAITING_STATUSES.has(status)) return "warning";
  if (status === "Visa Applied" || status === "Application Ready") return "info";
  return "default";
};

const getStatusIcon = (status) => {
  if (SUCCESS_STATUSES.has(status)) return <CheckCircle fontSize="small" />;
  if (ERROR_STATUSES.has(status)) return <Cancel fontSize="small" />;
  if (WAITING_STATUSES.has(status)) return <Schedule fontSize="small" />;
  return <Pending fontSize="small" />;
};

/** Best-effort subtitle from whatever the applications API returns per module. */
const getApplicationTitle = (row) => {
  const module = row?.module;
  if (module === "admission") {
    return (
      row?.courseName ||
      row?.course ||
      row?.universityName ||
      row?.university ||
      row?.title ||
      "Admission application"
    );
  }
  if (module === "visa") {
    return row?.visa || row?.type || row?.certificate || row?.title || "Visa application";
  }
  if (module === "insurance") {
    return row?.insuranceCompany || row?.coverType || row?.type || row?.title || "Insurance policy";
  }
  if (module === "skill-assessment") {
    return row?.occupation || row?.title || "Skill assessment";
  }
  if (module === "rpl-course") {
    return row?.certificate || row?.title || "RPL certificate";
  }
  return row?.title || row?.name || getModuleMeta(module).label;
};

const getApplicationSubtitle = (row) => {
  const parts = [];
  if (row?.module === "admission") {
    if (row?.universityName || row?.university) parts.push(row.universityName || row.university);
    if (row?.intake) parts.push(`Intake ${row.intake}`);
    if (row?.state) parts.push(row.state);
  } else if (row?.module === "visa") {
    if (row?.type && row?.visa) parts.push(row.type);
    if (row?.certificate) parts.push(row.certificate);
  } else if (row?.module === "insurance") {
    if (row?.coverType) parts.push(row.coverType);
    if (row?.visa) parts.push(`Visa: ${row.visa}`);
  } else if (row?.module === "skill-assessment") {
    if (row?.courseProvider) parts.push(row.courseProvider);
  } else if (row?.module === "rpl-course") {
    if (row?.usiNumber) parts.push(`USI ${row.usiNumber}`);
    if (row?.reference) parts.push(`Ref ${row.reference}`);
  }
  if (row?.reference && !parts.some((p) => String(p).includes(row.reference))) {
    parts.push(`Ref ${row.reference}`);
  }
  return parts.filter(Boolean).join(" · ") || null;
};

const DETAIL_FIELDS = [
  { key: "id", label: "Application ID" },
  { key: "module", label: "Type", format: (v) => getModuleMeta(v).label },
  { key: "status", label: "Status" },
  { key: "clientCaseOfficer", label: "Case Officer" },
  { key: "courseName", label: "Course" },
  { key: "course", label: "Course" },
  { key: "universityName", label: "University" },
  { key: "university", label: "University" },
  { key: "intake", label: "Intake" },
  { key: "visa", label: "Visa" },
  { key: "type", label: "Type / Subtype" },
  { key: "certificate", label: "Certificate" },
  { key: "insuranceCompany", label: "Insurer" },
  { key: "coverType", label: "Cover type" },
  { key: "occupation", label: "Occupation" },
  { key: "courseProvider", label: "Provider" },
  { key: "usiNumber", label: "USI" },
  { key: "reference", label: "Reference" },
  { key: "createdDate", label: "Created", format: (v) => formatSafeDate(v, "dd MMM yyyy, HH:mm") },
  {
    key: "updatedDate",
    label: "Last updated",
    format: (v) => formatSafeDate(v, "dd MMM yyyy, HH:mm"),
  },
];

/**
 * @param {Object} props
 * @param {string} [props.clientId] - When provided (e.g. Feedback view), use instead of route param.
 */
const ClientApplications = ({ clientId: clientIdProp } = {}) => {
  const { id: idFromRoute } = useParams();
  const clientId = clientIdProp ?? idFromRoute;
  const navigate = useNavigate();

  const { data: applicationsData = [], isFetching: isLoading } = useQuery({
    queryKey: ["clientApplications", clientId],
    queryFn: async () => {
      const res = await axios.get(`/clients/application/${clientId}`);
      return res?.data?.data || [];
    },
    enabled: !!clientId,
    refetchOnWindowFocus: false,
  });

  const [moduleFilter, setModuleFilter] = useState("all");
  const [historyApplication, setHistoryApplication] = useState(null);
  const [detailsApplication, setDetailsApplication] = useState(null);
  const [provenanceApplication, setProvenanceApplication] = useState(null);
  const canReadReferralApplications = usePermission(P.REFERRAL.APPLICATION_READ);

  const moduleCounts = useMemo(() => {
    const counts = { all: applicationsData.length };
    applicationsData.forEach((app) => {
      const key = app?.module || "other";
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [applicationsData]);

  const filteredData = useMemo(() => {
    if (moduleFilter === "all") return applicationsData;
    return applicationsData.filter((app) => app?.module === moduleFilter);
  }, [applicationsData, moduleFilter]);

  const openEdit = (row, { newTab = false } = {}) => {
    const meta = getModuleMeta(row?.module);
    const path = meta.editPath(row.id);
    if (newTab) {
      window.open(path, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(path, { state: { item: row } });
  };

  const copyId = async (id) => {
    try {
      await navigator.clipboard.writeText(String(id));
      toast.success("Application ID copied");
    } catch {
      toast.error("Could not copy ID");
    }
  };

  const getModuleName = (module) => {
    const moduleNames = {
      admission: "Admission",
      visa: "Visa",
      insurance: "Insurance",
      "skill-assessment": "Skill Assessment",
      "rpl-course": "RPL",
      other: "Other",
    };

    return moduleNames[module] || "default";
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "module",
        header: "Type",
        size: 150,
        Cell: ({ row }) => {
          const meta = getModuleMeta(row.original.module);
          const Icon = meta.icon;
          return (
            <div className="flex items-center gap-2 min-w-0">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-app-surface-muted text-primary">
                <Icon fontSize="small" />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-medium text-app-text truncate">{meta.label}</div>
                <div className="text-[11px] text-app-text-muted uppercase tracking-wide">
                  {meta.short}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        id: "details",
        header: "Details",
        size: 280,
        accessorFn: (row) => getApplicationTitle(row),
        Cell: ({ row }) => {
          const title = getApplicationTitle(row.original);
          const subtitle = getApplicationSubtitle(row.original);
          return (
            <div className="min-w-0 py-0.5">
              <div className="text-sm font-semibold text-app-text truncate" title={title}>
                {title}
              </div>
              {subtitle && (
                <div className="text-xs text-app-text-secondary truncate mt-0.5" title={subtitle}>
                  {subtitle}
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 180,
        Cell: ({ cell }) => {
          const status = cell.getValue() || "—";
          return (
            <Chip
              label={status}
              color={getStatusTone(status)}
              variant="filled"
              size="small"
              icon={getStatusIcon(status)}
              sx={{
                maxWidth: "100%",
                "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
              }}
            />
          );
        },
      },
      {
        id: "caseOfficer",
        header: "Case Officer",
        size: 140,
        accessorFn: (row) => row?.clientCaseOfficer || "—",
        Cell: ({ cell }) => <span className="text-sm text-app-text">{cell.getValue()}</span>,
      },
      {
        id: "source",
        header: "Source",
        size: 160,
        accessorFn: (row) => (row?.origin === "REQUESTED" ? "Referral request" : "Direct entry"),
        Cell: ({ row }) => {
          const app = row.original;
          if (!app?.origin) return <span className="text-app-text-muted">—</span>;
          const requested = app.origin === "REQUESTED";
          const shared = Boolean(app.visibleToReferrer);
          const title = requested
            ? `Requested by ${app.requestedBy || "the referrer"}${
                app.approvedBy ? `, approved by ${app.approvedBy}` : ""
              } · ${shared ? "Shared with the referral portal" : "Hidden from the referral portal"}`
            : `Entered directly by staff · ${
                shared ? "Shared with the referral portal" : "Hidden from the referral portal"
              }`;
          return (
            <Tooltip title={`${title}. Select for details and sharing.`}>
              <div className="min-w-0">
                <Chip
                  size="small"
                  variant={requested ? "filled" : "outlined"}
                  color={requested ? "primary" : "default"}
                  icon={
                    requested ? <History fontSize="small" /> : <PersonOutline fontSize="small" />
                  }
                  label={requested ? "Referral request" : "Direct entry"}
                  onClick={() => setProvenanceApplication(app)}
                  sx={{ maxWidth: "100%" }}
                />
                <div className="text-[11px] text-app-text-muted mt-0.5">
                  {shared ? "Shared with portal" : "Hidden from portal"}
                </div>
              </div>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "createdDate",
        header: "Created",
        size: 130,
        Cell: ({ cell }) => {
          const absolute = formatSafeDate(cell.getValue());
          const relative = formatRelative(cell.getValue());
          if (!absolute) return <span className="text-app-text-muted">—</span>;
          return (
            <Tooltip title={relative || absolute}>
              <div className="text-sm text-app-text">{absolute}</div>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "updatedDate",
        header: "Updated",
        size: 140,
        Cell: ({ cell }) => {
          const absolute = formatSafeDate(cell.getValue());
          const relative = formatRelative(cell.getValue());
          if (!absolute) return <span className="text-app-text-muted">—</span>;
          return (
            <Tooltip title={absolute}>
              <div>
                <div className="text-sm text-app-text">{absolute}</div>
                {relative && (
                  <div className="text-[11px] text-app-text-muted capitalize">{relative}</div>
                )}
              </div>
            </Tooltip>
          );
        },
      },
    ],
    []
  );

  console.log(filteredData);

  const table = useMaterialReactTable({
    columns,
    data: filteredData,
    enableRowActions: true,
    enableRowNumbers: true,
    enableGlobalFilter: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    positionActionsColumn: "last",
    positionGlobalFilter: "left",
    getRowId: (row) => String(row.id),
    renderRowActionMenuItems: ({ row, closeMenu }) => {
      const app = row.original;
      const meta = getModuleMeta(app.module);
      const items = [
        <MenuItem
          key="view"
          onClick={() => {
            closeMenu();
            openEdit(app);
          }}
        >
          <ListItemIcon>
            <VisibilityOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>View full details</ListItemText>
        </MenuItem>,
        <MenuItem
          key="quick"
          onClick={() => {
            closeMenu();
            setDetailsApplication(app);
          }}
        >
          <ListItemIcon>
            <DescriptionOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Quick summary</ListItemText>
        </MenuItem>,
        <MenuItem
          key="provenance"
          onClick={() => {
            closeMenu();
            setProvenanceApplication(app);
          }}
        >
          <ListItemIcon>
            <VisibilityOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sharing & origin</ListItemText>
        </MenuItem>,
      ];

      if (meta.hasHistory) {
        items.push(
          <MenuItem
            key="history"
            onClick={() => {
              closeMenu();
              setHistoryApplication(app);
            }}
          >
            <ListItemIcon>
              <History fontSize="small" />
            </ListItemIcon>
            <ListItemText>Show history</ListItemText>
          </MenuItem>
        );
      }

      items.push(<Divider key="div" />);
      items.push(
        <MenuItem
          key="new-tab"
          onClick={() => {
            closeMenu();
            openEdit(app, { newTab: true });
          }}
        >
          <ListItemIcon>
            <OpenInNew fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open in new tab</ListItemText>
        </MenuItem>,
        <MenuItem
          key="copy"
          onClick={() => {
            closeMenu();
            copyId(app.id);
          }}
        >
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy application ID</ListItemText>
        </MenuItem>
      );

      return items;
    },
    initialState: {
      showGlobalFilter: true,
      density: "compact",
      pagination: { pageSize: 10, pageIndex: 0 },
      sorting: [{ id: "updatedDate", desc: true }],
    },
    enablePagination: true,
    enableStickyHeader: true,
    enableColumnPinning: true,
    manualPagination: false,
    muiSearchTextFieldProps: {
      placeholder: "Search applications…",
      size: "small",
      sx: { minWidth: 200 },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: "70vh",
        minHeight: filteredData.length ? 320 : 200,
        overflowY: "auto",
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        const isActionClick =
          event.target.closest(".MuiIconButton-root") ||
          event.target.closest(".MuiPopover-root") ||
          event.target.closest(".MuiMenu-root") ||
          event.target.closest(".MuiChip-root");
        if (!isActionClick) setDetailsApplication(row.original);
      },
      sx: {
        cursor: "pointer",
        "&:hover": { backgroundColor: "var(--app-table-row-hover, rgba(0,0,0,0.04))" },
      },
    }),
    muiTablePaperProps: {
      elevation: 0,
      sx: { backgroundImage: "none", boxShadow: "none" },
    },
    state: {
      isLoading,
      showProgressBars: isLoading,
    },
    renderEmptyRowsFallback: () => (
      <div className="flex flex-col items-center justify-center py-14 px-4 text-center">
        <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-app-surface-muted text-app-text-muted">
          <DescriptionOutlined />
        </div>
        <p className="text-sm font-medium text-app-text m-0">No applications yet</p>
        <p className="text-xs text-app-text-secondary mt-1 m-0 max-w-sm">
          {moduleFilter === "all"
            ? "Admissions, visas, insurance, and other modules for this client will show up here."
            : `No ${getModuleMeta(moduleFilter).label.toLowerCase()} records for this client.`}
        </p>
      </div>
    ),
  });

  const detailRows = useMemo(() => {
    if (!detailsApplication) return [];
    const seen = new Set();
    return DETAIL_FIELDS.reduce((acc, field) => {
      if (seen.has(field.key)) return acc;
      const raw = detailsApplication[field.key];
      if (raw == null || raw === "") return acc;
      seen.add(field.key);
      acc.push({
        label: field.label,
        value: field.format ? field.format(raw) : String(raw),
      });
      return acc;
    }, []);
  }, [detailsApplication]);

  return (
    <div className="bg-app-surface rounded-lg border border-app-border overflow-hidden">
      {canReadReferralApplications && <ReferralApplicationSection clientId={clientId} />}
      <MaterialReactTable table={table} />

      {/* Quick summary */}
      <Dialog
        open={!!detailsApplication}
        onClose={() => setDetailsApplication(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="flex items-start justify-between gap-3 !pr-3">
          <div className="min-w-0">
            <Typography variant="overline" color="text.secondary" className="!leading-none">
              {detailsApplication ? getModuleMeta(detailsApplication.module).label : ""}
            </Typography>
            <Typography variant="h6" className="!font-semibold !leading-snug truncate">
              {detailsApplication ? getApplicationTitle(detailsApplication) : ""}
            </Typography>
            {detailsApplication?.status && (
              <Chip
                className="!mt-2"
                size="small"
                label={detailsApplication.status}
                color={getStatusTone(detailsApplication.status)}
                icon={getStatusIcon(detailsApplication.status)}
              />
            )}
          </div>
          <IconButton aria-label="Close" onClick={() => setDetailsApplication(null)} size="small">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className="!bg-app-surface-alt">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 m-0">
            {detailRows.map((row) => (
              <div key={row.label} className="min-w-0">
                <dt className="text-[11px] uppercase tracking-wide text-app-text-muted font-semibold">
                  {row.label}
                </dt>
                <dd className="m-0 mt-0.5 text-sm text-app-text break-words">{row.value}</dd>
              </div>
            ))}
          </dl>
          {detailRows.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No extra details available for this record.
            </Typography>
          )}
          <div className="flex flex-wrap gap-2 mt-6">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 transition"
              onClick={() => {
                const app = detailsApplication;
                setDetailsApplication(null);
                openEdit(app);
              }}
            >
              <VisibilityOutlined fontSize="small" />
              View full details
            </button>
            {detailsApplication && getModuleMeta(detailsApplication.module).hasHistory && (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-md border border-app-border bg-app-surface px-3 py-1.5 text-sm font-medium text-app-text hover:bg-app-surface-hover transition"
                onClick={() => {
                  const app = detailsApplication;
                  setDetailsApplication(null);
                  setHistoryApplication(app);
                }}
              >
                <History fontSize="small" />
                Show history
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Status history */}
      <Dialog
        open={!!historyApplication}
        onClose={() => setHistoryApplication(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="flex items-center justify-between gap-3 !pr-3">
          <div className="min-w-0">
            <Typography variant="overline" color="text.secondary" className="!leading-none">
              Status history
            </Typography>
            <Typography variant="h6" className="!font-semibold truncate">
              {historyApplication
                ? `${getModuleMeta(historyApplication.module).label} · ${getApplicationTitle(
                    historyApplication
                  )}`
                : ""}
            </Typography>
          </div>
          <IconButton aria-label="Close" onClick={() => setHistoryApplication(null)} size="small">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: "grey.50", p: 3 }}>
          {historyApplication?.module === "admission" && (
            <AdmissionStatusHistory
              admissionId={historyApplication.id}
              currentStatus={historyApplication.status}
            />
          )}
          {historyApplication?.module === "visa" && (
            <VisaStatusHistoryTab visaId={historyApplication.id} />
          )}
          {historyApplication?.module === "insurance" && (
            <InsuranceStatusHistoryTab insuranceId={historyApplication.id} />
          )}
          {historyApplication?.module === "skill-assessment" && (
            <SkillAssessmentStatusHistoryTab skillAssessmentId={historyApplication.id} />
          )}
          {historyApplication &&
            !["admission", "visa", "insurance", "skill-assessment"].includes(
              historyApplication.module
            ) && (
              <div className="bg-white rounded border border-slate-200 p-6 text-center">
                <Typography variant="body2" color="text.secondary">
                  Change history is not available for this application type yet.
                </Typography>
              </div>
            )}
        </DialogContent>
      </Dialog>

      {/* Origin & portal sharing */}
      <ApplicationProvenanceDialog
        application={provenanceApplication}
        clientId={clientId}
        onClose={() => setProvenanceApplication(null)}
      />
    </div>
  );
};

const FilterChip = ({ label, count, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border transition ${
      active
        ? "bg-primary/10 border-primary/30 text-primary"
        : "bg-app-surface border-app-border text-app-text-secondary hover:bg-app-surface-hover"
    }`}
  >
    {label}
    <span
      className={`inline-flex min-w-[1.25rem] justify-center rounded-full px-1 text-[10px] ${
        active ? "bg-primary text-white" : "bg-app-surface-muted text-app-text-muted"
      }`}
    >
      {count}
    </span>
  </button>
);

export default ClientApplications;
